export function createTable(results) {
  const { term, instalment, loan, nominal } = results;

  const cumData = [];

  let loanAmount = +loan;
  let interest = loanAmount * +nominal;
  let principal = +instalment - interest;

  let prevPrincipal = 0;
  let curCumPrincipal = principal;
  let totalPrincipal = principal + prevPrincipal;

  let totalPaid = +instalment;
  let curCum = 0;
  let prevInt = 0;
  let totalInterest = loanAmount * +nominal + prevInt;

  for (let i = 1; i <= term; i++) {
    const data = {
      term: i,
      instalment: instalment,
      interest: interest,
      principal: principal,
      totalPaid: totalPaid,
      totalInterest: totalInterest,
      totalPrincipal,
    };

    cumData.push(data);

    // Storing the value of the current cummulative principal
    curCumPrincipal = totalPrincipal;

    // Storing the value of the current cummulative interest
    curCum = totalInterest;

    // Re-assign the loan amount for the next month calculation
    loanAmount = loanAmount - principal;
    interest = loanAmount * nominal;
    totalInterest = interest + curCum;
    principal = instalment - interest;
    totalPaid = instalment * (i + 1);

    totalPrincipal = principal + curCumPrincipal;
  }

  return cumData;
}

export const sanitizeNumericInput = text => {
  // 0. Normalise comma decimal separator → dot (e.g. iOS locale "8,9" → "8.9")
  let cleaned = text.replace(",", ".");

  // 1. Remove everything except numbers and dots
  cleaned = cleaned.replace(/[^0-9.]/g, "");

  // 2. Prevent multiple decimal points (e.g., '12.3.4' -> '12.34')
  const parts = cleaned.split(".");
  if (parts.length > 2) {
    cleaned = parts[0] + "." + parts.slice(1).join("");
  }

  // 3. If starts with '.', convert to '0.'
  if (cleaned.startsWith(".")) {
    cleaned = "0" + cleaned;
  }

  // 4. Handle leading zeros (e.g., '0123' -> '123')
  // But allow '0' and '0.'
  if (cleaned.length > 1 && cleaned.startsWith("0") && cleaned[1] !== ".") {
    cleaned = cleaned.replace(/^0+/, "");
  }

  return cleaned;
};

// 2. With balloon payment integration
export const calculateTableValue = inputs => {
  const { loan, term, rate, extraPayments = [], rateChanges = [], serviceFee = 0, balloon = 0 } = inputs;

  let balance = parseFloat(loan);
  let currentRate = parseFloat(rate);
  let originalTerm = parseInt(term);
  let monthlyFee = parseFloat(serviceFee) || 0;
  const balloonAmount = (parseFloat(balloon) / 100) * balance;

  const schedule = [];
  let currentTargetTerm = originalTerm;
  // This tracks the "Accelerated Maturity Date"
  let effectiveRemainingMonths = originalTerm;

  // 1. CALCULATE THE FIXED INSTALMENT ONCE
  // This value stays constant regardless of extra payments.
  let monthlyInstalment = calculateStandardPayment(balance, currentRate, originalTerm, balloonAmount);

  for (let month = 1; month <= originalTerm; month++) {
    // 0. EXIT CHECK: Stop if we've reached the balloon/zero
    if (balance <= balloonAmount + 0.01 && month > 1) break;

    // 2. ONLY RE-CALCULATE INSTALMENT IF THE RATE CHANGES
    const rateUpdate = rateChanges.find(c => parseInt(c.term) === month);
    if (rateUpdate) {
      currentRate = parseFloat(rateUpdate.rate);

      // We calculate how many months were LEFT in our accelerated schedule
      // based on the instalment we were paying JUST BEFORE the rate changed.
      const monthsRemainingAtOldRate = calculateRemainingMonths(
        balance,
        parseFloat(rate), // Use the previous rate or tracking variable
        monthlyInstalment,
        balloonAmount,
      );

      // Now we set the new instalment using THAT shortened timeframe
      effectiveRemainingMonths = Math.max(1, Math.round(monthsRemainingAtOldRate));

      monthlyInstalment = calculateStandardPayment(balance, currentRate, effectiveRemainingMonths, balloonAmount);
    }

    const monthlyInterestRate = currentRate / 12 / 100;
    const interestPortion = balance * monthlyInterestRate;
    let principalPortion = monthlyInstalment - interestPortion;

    const scheduledExtra = extraPayments.find(p => parseInt(p.term) === month);
    let extraAmount = parseFloat(scheduledExtra?.amount) || 0;

    let totalPrincipalReduction = principalPortion + extraAmount;

    // let finalInstalment = monthlyInstalment;

    // 5. Final Month / Balloon Logic
    let isFinalMonth = month === originalTerm;

    // 6. Final Month / Balloon / Early Payoff Logic
    // If extra payments pay off the loan early (down to balloon)
    // if (balance - (principalPortion + extraAmount) <= balloonAmount) {
    if (balance - totalPrincipalReduction <= balloonAmount) {
      principalPortion = balance - balloonAmount;
      // finalInstalment = principalPortion + interestPortion;
      let finalInstalment = principalPortion + interestPortion;
      // balance = balloonAmount;
      // extraAmount = 0;
      // isFinalMonth = true;

      schedule.push({
        term: month,
        instalment: finalInstalment + monthlyFee,
        interest: interestPortion,
        principal: principalPortion + extraAmount,
        additionalPayment: 0,
        remainingBalance: balloonAmount,
        serviceFee: monthlyFee,
        isBalloon: balloonAmount > 0,
      });
      break;
    }
    // balance = balance - (principalPortion + extraAmount);
    balance = balance - totalPrincipalReduction;

    schedule.push({
      term: month,
      instalment: monthlyInstalment + extraAmount + monthlyFee,
      interest: interestPortion,
      principal: principalPortion + extraAmount,
      additionalPayment: extraAmount,
      remainingBalance: balance,
      serviceFee: monthlyFee,
      isExtra: !!extraAmount,
    });
  }

  return schedule;
};

export const formatToMoney = (value = 0, currency) => {
  const currencyObj = { style: "currency", currency: currency?.code || "USD" };
  const locale = currency?.locale || "en-US";

  return value.toLocaleString(locale, currencyObj);
};

const calculateStandardPayment = (pv, annualRate, months, balloonAmount = 0) => {
  const r = annualRate / 12 / 100;

  if (r === 0) return (pv - balloonAmount) / months;

  // Calculate the payment required to leave the balloonAmount at the end of 'months'
  const numerator = (pv - balloonAmount / Math.pow(1 + r, months)) * r;
  const denominator = 1 - Math.pow(1 + r, -months);

  return numerator / denominator;
};

const calculateRemainingMonths = (pv, annualRate, pmt, balloonAmount = 0) => {
  const r = annualRate / 12 / 100;
  if (r === 0) return (pv - balloonAmount) / pmt;

  const numerator = pmt - r * balloonAmount;
  const denominator = pmt - r * pv;

  if (numerator <= 0 || denominator <= 0) return 0;

  const n = Math.log(numerator / denominator) / Math.log(1 + r);
  return n;
};
